import React, { useEffect, useRef } from 'react';
import { t, T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  removeConfigurationFromList,
  removeUserImageForConfiguration,
  setUserImageForConfiguration,
} from '../../Reducers/App/Actions';
import ImageIcon from '@material-ui/icons/Image';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const useUploadButtonStyles = makeStyles((theme) => ({
  root: {
    '& > label': {
      display: 'block',
    },
    '& > *': {
      margin: 0,
    },
  },
  input: {
    display: 'none',
  },
}));

const UploadButton = ({ onChange, name, value, ...props }) => {
  const classes = useUploadButtonStyles();
  const id = `uploadButton-${name}`;
  const ref = useRef(null);
  useEffect(() => {
    if (!value) {
      ref.current.value = '';
    }
  }, [value]);
  return (
    <div className={classes.root}>
      <input
        accept=".pdf, .jpg, .png"
        className={classes.input}
        id={id}
        name={name}
        type="file"
        onChange={onChange}
        ref={ref}
      />
      <label htmlFor={id}>
        <Button color="primary" component="span" {...props} />
      </label>
    </div>
  );
};

UploadButton.propTypes = {
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
};

const useConfigurationListStyles = makeStyles((theme) => ({
  container: {
    overflowY: 'auto',
  },
  configuration: {
    marginBottom: 16,
    paddingRight: 8,
  },
  removeButton: {
    textDecoration: 'underline',
    padding: 0,
    '&.MuiButton-root': {
      height: 'auto',
    },
    '& .MuiButton-label': {
      fontWeight: 'normal',
    },
    justifyContent: 'flex-start',
    width: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.remove-image': {
      height: 20,
      marginTop: 0,
    },
  },
  imageUploadButton: {
    '& .MuiButton-startIcon': {
      width: 40,
      height: 40,
    },
    '& .MuiButton-iconSizeLarge > *:first-child': {
      fontSize: 40,
    },
    justifyContent: 'flex-start',
    padding: 0,
    '& .MuiButton-label .MuiTypography-body2': {
      fontStyle: 'italic',
      fontSize: '0.8em',
    },
  },
  previewImage: {
    maxWidth: '100%',
  },
  option: {
    marginBottom: 8,
  },
}));

const ConfigurationList = () => {
  const configurations = useSelector((state) => state.app.configurationList);
  const userImages = useSelector((state) => state.app.userImages);
  const dispatch = useDispatch();
  const classes = useConfigurationListStyles();
  const removeConfiguration = (configurationCode) => dispatch(removeConfigurationFromList(configurationCode));
  const removeUserImage = (configurationCode) => dispatch(removeUserImageForConfiguration(configurationCode));
  const onImageUploadChange = (evt) =>
    evt.target.files[0] && dispatch(setUserImageForConfiguration(evt.target.name, evt.target.files[0]));
  return (
    <div className={classes.container}>
      {configurations.map((configuration) => {
        const userImage = userImages[configuration.code];
        return (
          <div key={configuration.code} className={classes.configuration}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography>
                  <strong>{configuration.item.title}</strong>
                </Typography>
                <Typography>{configuration.calculation.totalFormatted}</Typography>
                <Button
                  onClick={() => removeConfiguration(configuration.code)}
                  size="small"
                  className={classes.removeButton}
                >
                  {t('configurationList.remove')}
                </Button>
                <img
                  src={configuration.previewImage}
                  alt={configuration.code}
                  className={classes.previewImage}
                />
              </Grid>
              <Grid item xs={6}>
                {configuration.optionclassifications
                  ?.find(({ identifier }) => identifier === 'measurements')
                  ?.selectedoptions?.map((option) => (
                    <div key={option.identifier} className={classes.option}>
                      <Typography key={option.identifier}>{option.title}</Typography>
                      <Typography>
                        <strong>{option.inputText} mm</strong>
                      </Typography>
                    </div>
                  ))}
              </Grid>
            </Grid>
            <UploadButton
              name={configuration.code}
              size="large"
              className={classes.imageUploadButton}
              startIcon={<ImageIcon />}
              onChange={onImageUploadChange}
              value={userImage}
            >
              <Typography
                component="span"
                variant="body2"
                dangerouslySetInnerHTML={{
                  __html: userImage
                    ? t('configurationList.yourSelectedPhoto', {
                        fileName: userImage.name,
                      })
                    : t('configurationList.imageUploadButtonLabel'),
                }}
              />
            </UploadButton>
            {userImage && (
              <Button
                onClick={() => removeUserImage(configuration.code)}
                size="small"
                className={clsx(classes.removeButton, 'remove-image')}
              >
                {t('configurationList.removeImage')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConfigurationList;
